import React from "react"
import { useTranslation } from "react-i18next"

import { useEvent } from "shared/lib/useEvent"
import { ChipList } from "shared/ui/chip-list/ChipList"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { MediaOption } from "shared/ui/media-option/MediaOption"

import {
  BuildingTypeFilterItem,
  ProductFilterItem,
  ProductFilterType,
  ProductGroupFilterItem,
  RoomTypeFilterItem
} from "../../types/ProductFilterType"
import { OnRemoveProductFilter } from "./ProductFilterChipListView"

export interface ProductFilterChipProps {
  chip: ProductFilterItem
  onRemove: OnRemoveProductFilter
}

interface ProductGroupChipContentProps {
  chip: ProductGroupFilterItem
}

const ProductGroupChipContent = React.memo(
  ({ chip }: ProductGroupChipContentProps) => {
    const { t } = useTranslation()
    return (
      <MediaOption.Root variant="stacked">
        <MediaOption.Label>{chip.label}</MediaOption.Label>
        <MediaOption.Subtext>{t("Product.Group")}</MediaOption.Subtext>
      </MediaOption.Root>
    )
  }
)
ProductGroupChipContent.displayName = "ProductGroupChipContent"

interface RoomTypeChipContentProps {
  chip: RoomTypeFilterItem
}

const RoomTypeChipContent = React.memo(({ chip }: RoomTypeChipContentProps) => {
  const { t } = useTranslation()
  return (
    <MediaOption.Root variant="stacked">
      <MediaOption.Label>{chip.label}</MediaOption.Label>
      <MediaOption.Subtext>{t("RoomType.RoomType")}</MediaOption.Subtext>
    </MediaOption.Root>
  )
})
RoomTypeChipContent.displayName = "RoomTypeChipContent"

interface BuildingTypeChipContentProps {
  chip: BuildingTypeFilterItem
}

const BuildingTypeChipContent = React.memo(
  ({ chip }: BuildingTypeChipContentProps) => {
    const { t } = useTranslation()
    return (
      <MediaOption.Root variant="stacked">
        {chip.icon && (
          <MediaOption.Media>
            <Icon name={chip.icon} />
          </MediaOption.Media>
        )}
        <MediaOption.Label>{chip.label}</MediaOption.Label>
        <MediaOption.Subtext>
          {t("BuildingReference.BuildingType.BuildingType")}
        </MediaOption.Subtext>
      </MediaOption.Root>
    )
  }
)
BuildingTypeChipContent.displayName = "BuildingTypeChipContent"

function ProductFilterChipContentFactory({
  chip
}: {
  chip: ProductFilterItem
}) {
  switch (chip.filterType) {
    case ProductFilterType.PRODUCT_GROUP:
      return <ProductGroupChipContent chip={chip} />
    case ProductFilterType.ROOM_TYPE:
      return <RoomTypeChipContent chip={chip} />
    case ProductFilterType.BUILDING_TYPE:
      return <BuildingTypeChipContent chip={chip} />
    default:
      return null
  }
}

export const ProductFilterChip = React.memo(
  ({ chip, onRemove }: ProductFilterChipProps) => {
    const handleClick = useEvent(() => {
      onRemove(chip.filterType, chip.value)
    })

    return (
      <ChipList.Chip as="button" onClick={handleClick}>
        <ProductFilterChipContentFactory chip={chip} />
        <Icon name={IconName.CLOSE} />
      </ChipList.Chip>
    )
  }
)

ProductFilterChip.displayName = "ProductFilterChip"
